// The merging service writes each version's video, full mix and dialogue-only
// track into one run folder. Only the video and full mix are recorded in
// Firestore; the dialogue track shares the full mix's name with a DX suffix.
const FULL_MIX_SUFFIX = "_MO_PM_NOCH.wav";
const DIALOGUE_SUFFIX = "_MO_DX_NOCH.wav";

const folderOf = (reference) => {
  if (typeof reference !== "string" || !reference) {
    return null;
  }
  const path = reference.split("?")[0];
  const index = path.lastIndexOf("/");
  return index > 0 ? path.slice(0, index) : null;
};

const findFullMix = (video, dubbedAudiosList) => {
  const audios = (Array.isArray(dubbedAudiosList) ? dubbedAudiosList : [])
    .filter((audio) => typeof audio?.path === "string" && audio.path);

  const videoFolder = folderOf(video.url);
  const sameRun = audios.find((audio) => folderOf(audio.path) === videoFolder);
  if (sameRun) {
    return sameRun;
  }

  // Legacy entries may not share a folder; only trust an unambiguous match.
  const sameVersion = audios.filter(
    (audio) =>
      audio.version === video.version && audio.language === video.language
  );
  return sameVersion.length === 1 ? sameVersion[0] : null;
};

export const getVersionMediaFiles = (video, dubbedAudiosList) => {
  if (!video) {
    return {};
  }
  const fullMixPath = findFullMix(video, dubbedAudiosList)?.path || null;
  const dialoguePath =
    fullMixPath && fullMixPath.endsWith(FULL_MIX_SUFFIX)
      ? fullMixPath.slice(0, -FULL_MIX_SUFFIX.length) + DIALOGUE_SUFFIX
      : null;

  return {
    video: video.url || null,
    fullMix: fullMixPath,
    dialogue: dialoguePath,
  };
};

export const STEM_LABELS = {
  dialogue: "Dialogue",
  music: "Music",
  effect: "Effects",
  musicEffects: "Music & Effects",
  dialogueMusicEffect: "Full mix",
};

const stemsOf = (uploadedAudioTypes) =>
  (Array.isArray(uploadedAudioTypes) ? uploadedAudioTypes : []).filter(
    (stem) => stem?.url
  );

// The combined M&E stem when uploaded, otherwise music and effects together.
export const getMusicEffectsStems = (uploadedAudioTypes) => {
  const stems = stemsOf(uploadedAudioTypes);
  const firstOfType = (audioType) =>
    stems.find((stem) => stem.audioType === audioType);

  const combined = firstOfType("musicEffects");
  if (combined) {
    return [combined];
  }
  return ["music", "effect"].map(firstOfType).filter(Boolean);
};

const getStemLabel = (stem) =>
  STEM_LABELS[stem.audioType] || stem.audioType || "Stem";

// The download menu follows the selected version: a dubbed version offers its
// own video and audio renders plus the M&E it was mixed over; the original
// offers the footage and stems.
export const buildMediaDownloadItems = ({
  version,
  dubbedAudiosList,
  videoUrl,
  uploadedAudioTypes,
}) => {
  const stems = stemsOf(uploadedAudioTypes);

  if (version) {
    const files = getVersionMediaFiles(version, dubbedAudiosList);
    const group = `Version ${version.version}`;
    return [
      {
        key: "version-video",
        group,
        kind: "video",
        label: "Dubbed video",
        reference: files.video,
      },
      {
        key: "version-full-mix",
        group,
        kind: "audio",
        label: "Full mix",
        description: "Dialogue, music & effects",
        reference: files.fullMix,
      },
      {
        key: "version-dialogue",
        group,
        kind: "audio",
        label: "Dialogue",
        description: "Translated dialogue only",
        reference: files.dialogue,
      },
      ...getMusicEffectsStems(stems).map((stem) => ({
        key: `version-stem-${stem.url}`,
        group,
        kind: "audio",
        label: getStemLabel(stem),
        description: "Original, without dialogue",
        reference: stem.url,
      })),
    ];
  }

  return [
    {
      key: "original-video",
      group: "Original",
      kind: "video",
      label: "Original footage",
      reference: videoUrl,
    },
    ...stems.map((stem) => ({
      key: `stem-${stem.url}`,
      group: "Stems",
      kind: "audio",
      label: getStemLabel(stem),
      description: stem.uploadType,
      reference: stem.url,
    })),
  ];
};
